import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import FileViewer from "react-file-viewer";
import { Document, Page, pdfjs } from "react-pdf";
import { HiDownload, HiX } from "react-icons/hi";
import { BASE_URL } from "../helper/constants";
import "./CompanyDocumentViewerModal.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function getExtension(name) {
  if (!name) return "";
  const parts = String(name).split(".");
  return parts.length > 1 ? parts.pop().trim().toLowerCase() : "";
}

function resolveFileUrl(fileUrl) {
  if (!fileUrl) return "";
  const raw = Array.isArray(fileUrl) ? fileUrl[0] : fileUrl;
  if (!raw) return "";
  if (/^(https?:|blob:|data:)/i.test(raw)) return raw;
  return BASE_URL + raw;
}

/**
 * Firmendokumente preview modal.
 * Files are fetched as a blob first because /getFile 302s to S3 and
 * in-browser PDF.js cannot follow that redirect, so it stuck on "Loading PDF...".
 */
export default function CompanyDocumentViewerModal({
  fileUrl,
  fileName,
  onClose,
  onDownload,
  downloadLabel = "Download",
}) {
  const name = fileName || "document";
  const fileType = getExtension(name);
  const absoluteUrl = useMemo(() => resolveFileUrl(fileUrl), [fileUrl]);

  const googleViewerUrl = useMemo(
    () =>
      absoluteUrl
        ? "https://docs.google.com/viewer?url=" +
          encodeURIComponent(absoluteUrl) +
          "&embedded=true"
        : "",
    [absoluteUrl]
  );

  const [blobUrl, setBlobUrl] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [pageWidth, setPageWidth] = useState(null);
  const docFileRef = useRef(null);

  useEffect(() => {
    if (!absoluteUrl) {
      setFetching(false);
      return undefined;
    }
    let objectUrl = null;
    let cancelled = false;
    setFetching(true);
    setLoadError(false);
    setBlobUrl(null);
    setNumPages(null);
    setPdfReady(false);

    axios({
      url: absoluteUrl,
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        if (cancelled) return;
        const mime =
          fileType === "pdf"
            ? "application/pdf"
            : response.data.type || "application/octet-stream";
        const blob = new Blob([response.data], { type: mime });
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setFetching(false);
      })
      .catch((e) => {
        console.error(e);
        if (cancelled) return;
        setLoadError(true);
        setFetching(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [absoluteUrl, fileType]);

  useEffect(() => {
    const el = docFileRef.current;
    if (!el || fileType !== "pdf") return undefined;
    const measure = () => setPageWidth(Math.max(280, Math.floor(el.clientWidth - 32)));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fileType, blobUrl]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleDownload = useCallback(async () => {
    if (typeof onDownload === "function") {
      onDownload();
      return;
    }
    setDownloading(true);
    try {
      const href = blobUrl || absoluteUrl;
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error(e);
    } finally {
      setDownloading(false);
    }
  }, [onDownload, blobUrl, absoluteUrl, name]);

  if (!fileUrl) return null;

  const useOfficeIframe =
    fileType === "txt" ||
    fileType === "doc" ||
    fileType === "docx" ||
    fileType === "xls" ||
    fileType === "xlsx" ||
    fileType === "ppt" ||
    fileType === "pptx";

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={name}
      onClick={onClose}
      className="company-document-viewer-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="company-document-viewer-modal__dialog"
      >
        <div className="company-document-viewer-modal__header">
          <div className="company-document-viewer-modal__name">
            {name}
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            title={downloadLabel}
            className="kj-btn kj-btn--outline kj-btn--internal kj-btn--on-surface kj-btn--sm"
          >
            <HiDownload size={18} />
            {downloadLabel}
          </button>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close"
            className="kj-btn kj-btn--icon kj-btn--lg kj-btn--neutral kj-btn--round"
          >
            <HiX size={20} />
          </button>
        </div>

        <div
          ref={docFileRef}
          className={`doc-file company-docs-preview-wrap company-document-viewer-modal__body${fileType === "pdf" ? " company-document-viewer-modal__body--pdf" : ""}`}
        >
          {loadError && fileType === "pdf" ? (
            <div className="company-document-viewer-modal__error">
              Preview failed. Use Download to open the file.
            </div>
          ) : fileType === "pdf" && blobUrl ? (
            <>
              <div
                className={`company-docs-preview-pages company-document-viewer-modal__pages${pdfReady ? " company-document-viewer-modal__pages--ready" : ""}`}
              >
                <Document
                  file={blobUrl}
                  loading={null}
                  onLoadSuccess={({ numPages: n }) => {
                    setNumPages(n);
                    setPdfReady(true);
                  }}
                  onLoadError={() => {
                    setLoadError(true);
                    setPdfReady(false);
                  }}
                  options={{ disableRange: true, disableStream: true }}
                >
                  {Array.from(new Array(numPages || 1), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      width={pageWidth || undefined}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  ))}
                </Document>
              </div>
              {(fetching || !pdfReady) && (
                <div className="company-docs-preview-overlay">
                  <div className="company-docs-spinner" />
                </div>
              )}
            </>
          ) : fetching ? (
            <div className="company-docs-preview-overlay">
              <div className="company-docs-spinner" />
            </div>
          ) : useOfficeIframe ? (
            <iframe title={name} src={googleViewerUrl} className="company-document-viewer-modal__frame" />
          ) : (
            <FileViewer
              width="100"
              height="600"
              fileType={fileType}
              filePath={blobUrl || absoluteUrl}
              onError={() => console.log("document preview error")}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
